import { defineQuery } from "@rocicorp/zero";
import { z } from "zod";
import type {} from "../zero-context";
import { zql } from "../schema";
import { ADMIN_PLUS } from "../constants";

/**
 * Pending join requests for a workspace, visible to ADMIN_PLUS members
 * (primary_owner/owner/admin) only. Powers the admin queue surface on
 * the access-tab and inline approval items in the inbox.
 *
 * Non-admin callers get an empty list (the inner `whereExists` short-
 * circuits server-side at query-auth time).
 */
const pendingForWorkspace = defineQuery(
  z.string(),
  ({ args: workspaceId, ctx: { sub } }) =>
    zql.workspace_join_requests
      .where("workspace_id", workspaceId)
      .where("status", "pending")
      .whereExists("workspace", (wq) =>
        wq.whereExists("members", (mq) =>
          mq
            .where("user_id", sub)
            .where("role", "IN", ADMIN_PLUS)
            .where("removed_at", "IS", null),
        ),
      )
      .related("user"),
);

/**
 * The current user's most recent request for a given workspace. Used by
 * the public-workspace splash to render the "Waiting for approval" or
 * "Request denied" state after a logged-in non-member submits a request.
 *
 * No membership check — by definition a requester is NOT a member yet,
 * so the only gate is `user_id = sub`. Returns the latest row by
 * `requested_at`.
 */
const mineForWorkspace = defineQuery(
  z.string(),
  ({ args: workspaceId, ctx: { sub } }) =>
    zql.workspace_join_requests
      .where("workspace_id", workspaceId)
      .where("user_id", sub)
      .orderBy("requested_at", "desc")
      .one(),
);

export const workspaceJoinRequestsQueries = {
  pendingForWorkspace,
  mineForWorkspace,
};
